import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import { syncAll } from '@/shared/db/syncEngine';
import { useQueryClient } from '@tanstack/react-query';

export interface SyncContextValue {
  isSyncing: boolean;
  lastSyncAt: Date | null;
  syncError: string | null;
  syncNow: () => void;
}

export const SyncContext = createContext<SyncContextValue | null>(null);

const RECONNECT_DEBOUNCE_MS = 1500;

export function SyncProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isOnline = useOnlineStatus();
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSyncingRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const runSync = useCallback(async () => {
    if (!user || isSyncingRef.current) return;
    isSyncingRef.current = true;
    if (isMountedRef.current) {
      setIsSyncing(true);
      setSyncError(null);
    }

    try {
      const result = await syncAll(user.id);

      if (isMountedRef.current) {
        if (result.success === false) {
          if (result.error !== 'offline') setSyncError(result.error);
        } else {
          setLastSyncAt(new Date());
          setSyncError(null);
          queryClient.invalidateQueries({ queryKey: ['templates', user.id] });
          queryClient.invalidateQueries({ queryKey: ['signed_contracts', user.id] });
          queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
        }
      }
    } catch {
      // syncAll has its own try/catch — this is a last-resort safety net
    } finally {
      isSyncingRef.current = false;
      if (isMountedRef.current) setIsSyncing(false);
    }
  }, [user, queryClient]);

  // Tenir runSync à jour dans une ref pour éviter des dépendances dans le useEffect de reconnexion
  const runSyncRef = useRef(runSync);
  useEffect(() => { runSyncRef.current = runSync; }, [runSync]);

  // Déclencher sync uniquement au passage offline → online (pas au mount initial)
  const isFirstMountRef = useRef(true);
  useEffect(() => {
    // Skip the very first evaluation — isOnline starts true, we don't want to sync on every app boot
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    if (!isOnline) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSyncRef.current();
    }, RECONNECT_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isOnline]);

  const syncNow = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    runSync();
  }, [runSync]);

  return (
    <SyncContext.Provider value={{ isSyncing, lastSyncAt, syncError, syncNow }}>
      {children}
    </SyncContext.Provider>
  );
}
